/** FIREBASE_FCM_CREDENTIAL control: paste the FCM v1 service-account JSON (or the legacy FCM server
 *  key) from the Firebase project so the backend creates the Twilio FCM Push Credential (stores only
 *  the resulting SID). The secret is used, never displayed back or stored. Mirrors the APNs control.
 */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import type { PreflightItem, TwilioApnDetail } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';

export function FirebaseFcmCredentialControl({
  item,
  topId,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  const detail = item.detail as unknown as TwilioApnDetail;
  const [secret, setSecret] = useState('');
  const [opError, setOpError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => items.createTwilioFcmCredential(topId, item.id, { secret: secret.trim() }),
    onMutate: () => setOpError(null),
    onSuccess: (res) => {
      if (res.ok) {
        snackbar.show('FCM push credential created.', 'success');
        setSecret('');
        onChanged();
      } else {
        setOpError(typeof res.detail === 'string' ? res.detail : null);
        snackbar.show(
          res.reason === 'not_configured' ? 'Twilio is not configured.' : 'Could not create the credential.',
          'error',
        );
      }
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Could not create the credential.', 'error'),
  });

  if (detail?.configured === false) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Alert severity="info">Twilio isn&apos;t configured on the server.</Alert>
      </Box>
    );
  }

  const existing = detail?.fcm_credential_sid;
  const canSubmit = !!secret.trim() && !create.isPending;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack spacing={1.5}>
        {opError && (
          <Alert
            severity="error"
            onClose={() => setOpError(null)}
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {opError}
          </Alert>
        )}
        <Stack direction="row" spacing={1} alignItems="center">
          {existing ? (
            <CheckCircleIcon fontSize="small" color="success" />
          ) : (
            <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
          )}
          <Typography variant="body2">
            {existing ? `FCM credential set (${existing})` : 'No FCM push credential yet'}
          </Typography>
        </Stack>

        {canManage && (
          <>
            <Typography variant="caption" color="text.secondary">
              Paste the FCM v1 service-account JSON (or the legacy FCM server key) from the Firebase
              project. It is used to create the Twilio credential and is not stored.
            </Typography>
            <TextField
              size="small"
              label="FCM service account / server key"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              multiline
              minRows={3}
              disabled={create.isPending}
              fullWidth
            />
            <Box>
              <Button
                size="small"
                variant="contained"
                onClick={() => create.mutate()}
                disabled={!canSubmit}
                startIcon={create.isPending ? <CircularProgress size={14} /> : undefined}
              >
                {existing ? 'Replace credential' : 'Create credential'}
              </Button>
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );
}
